import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Building2, User, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { MyShowLogoNeon } from '@/components/Logo';

export function SignupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const signIn = useAuthStore(s => s.signIn);

  const [tipo, setTipo] = useState<'pj' | 'pf'>('pj');
  const [form, setForm] = useState({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    cpf: '',
    email_primary: '',
    contact_name: '',
    phone: '',
    password: '',
    segment: 'mercado',
    accept_terms: false,
    accept_privacy: false,
    // Endereço
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
  });
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  async function lookupCep(raw: string) {
    const clean = raw.replace(/\D/g, '');
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm(prev => ({
          ...prev,
          logradouro: data.logradouro ?? prev.logradouro,
          bairro:     data.bairro     ?? prev.bairro,
          cidade:     data.localidade ?? prev.cidade,
          estado:     data.uf         ?? prev.estado,
        }));
        toast.success('Endereço preenchido pelo CEP');
      } else {
        toast.error('CEP não encontrado.');
      }
    } catch {
      toast.error('Falha ao consultar CEP. Verifique sua conexão.');
    }
    setCepLoading(false);
  }

  function update<K extends keyof typeof form>(k: K, v: typeof form[K]) {
    setForm(prev => ({ ...prev, [k]: v }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.accept_terms || !form.accept_privacy) {
      toast.error('Você precisa aceitar os termos e política de privacidade.');
      return;
    }
    if (tipo === 'pj' && !form.cnpj.trim()) {
      toast.error('Informe o CNPJ.');
      return;
    }
    if (tipo === 'pf' && !form.cpf.trim()) {
      toast.error('Informe o CPF.');
      return;
    }
    if (!form.phone.trim()) {
      toast.error('Informe o telefone.');
      return;
    }
    setLoading(true);
    try {
      const baseName = (form.nome_fantasia || form.razao_social).toLowerCase();
      const subdomain = baseName
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]/g, '')
        .slice(0, 28) || `tenant${Date.now()}`;

      await apiClient.signup({
        razao_social: form.razao_social,
        nome_fantasia: form.nome_fantasia || undefined,
        cnpj: tipo === 'pj' ? form.cnpj.replace(/\D/g, '') : undefined,
        cpf: tipo === 'pf' ? form.cpf.replace(/\D/g, '') : undefined,
        subdomain,
        email_primary: form.email_primary,
        contact_name: form.contact_name,
        phone: form.phone.replace(/\D/g, ''),
        password: form.password,
        segment: form.segment,
        accept_terms: form.accept_terms,
        accept_privacy: form.accept_privacy,
        plan_code: params.get('plan') || 'starter',
        address_cep: form.cep.replace(/\D/g, '') || undefined,
        address_logradouro: form.logradouro || undefined,
        address_numero: form.numero || undefined,
        address_complemento: form.complemento || undefined,
        address_bairro: form.bairro || undefined,
        address_cidade: form.cidade || undefined,
        address_estado: form.estado || undefined,
      });
      toast.success('Conta criada! Fazendo login…');
      const { error } = await signIn(form.email_primary, form.password);
      if (error) {
        navigate('/login');
        return;
      }
      navigate('/onboarding');
    } catch (err: any) {
      const code = err.response?.data?.error;
      const details = err.response?.data?.details;
      if (code === 'cnpj_taken') toast.error('Este CNPJ já está cadastrado');
      else if (code === 'email_taken') toast.error('Este email já tem uma conta. Faça login.');
      else if (code === 'terms_required') toast.error('Você precisa aceitar os termos.');
      else toast.error('Erro ao criar conta: ' + (details || code || 'tente novamente'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-black text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-600/20 rounded-full blur-3xl" />
        <Link to="/" className="relative flex items-center">
          <MyShowLogoNeon height={120} />
        </Link>
        <div className="space-y-4">
          <h2 className="text-3xl font-bold">14 dias grátis</h2>
          <ul className="space-y-2 text-white/90">
            <li className="flex gap-2"><Check className="h-5 w-5" />Sem cartão de crédito</li>
            <li className="flex gap-2"><Check className="h-5 w-5" />TVs ilimitadas no trial</li>
            <li className="flex gap-2"><Check className="h-5 w-5" />Cancele quando quiser</li>
          </ul>
        </div>
        <div className="text-xs text-white/60">© Visual Software</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-bold">Crie sua conta</h1>
          <p className="mt-2 text-muted-foreground">Em 2 minutos você está pronto</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            {/* Tipo de pessoa */}
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Tipo de cadastro</div>
              <div className="grid grid-cols-2 gap-2">
                {([['pj', Building2, 'Pessoa Jurídica', 'CNPJ'], ['pf', User, 'Pessoa Física', 'CPF']] as const).map(([t, Icon, label, doc]) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTipo(t)}
                    className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm transition-colors ${
                      tipo === t
                        ? 'border-brand-600 bg-brand-50/40 text-brand-700 font-medium'
                        : 'border-border hover:border-muted-foreground/40 text-muted-foreground'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{label} ({doc})</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">Dados da empresa / pessoa</div>

              <Input
                placeholder={tipo === 'pj' ? 'Razão social *' : 'Nome completo *'}
                required
                value={form.razao_social}
                onChange={e => update('razao_social', e.target.value)}
              />
              {tipo === 'pj' && (
                <Input
                  placeholder="Nome fantasia"
                  value={form.nome_fantasia}
                  onChange={e => update('nome_fantasia', e.target.value)}
                />
              )}

              {tipo === 'pj' ? (
                <Input
                  placeholder="CNPJ * (ex: 12.345.678/0001-90)"
                  required
                  value={form.cnpj}
                  onChange={e => update('cnpj', e.target.value)}
                />
              ) : (
                <Input
                  placeholder="CPF * (ex: 123.456.789-00)"
                  required
                  value={form.cpf}
                  onChange={e => update('cpf', e.target.value)}
                />
              )}
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">Contato</div>
              <Input
                placeholder="Seu nome *"
                required
                value={form.contact_name}
                onChange={e => update('contact_name', e.target.value)}
              />
              <Input
                type="email"
                placeholder="Email *"
                required
                value={form.email_primary}
                onChange={e => update('email_primary', e.target.value)}
              />
              <Input
                type="tel"
                placeholder="Telefone / WhatsApp *"
                required
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
              />
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">Endereço</div>
              <div className="flex gap-2">
                <Input
                  placeholder="CEP *"
                  required
                  value={form.cep}
                  onChange={e => update('cep', e.target.value)}
                  onBlur={e => lookupCep(e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={cepLoading}
                  onClick={() => lookupCep(form.cep)}
                  title="Buscar endereço pelo CEP"
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              <Input
                placeholder="Número *"
                required
                value={form.numero}
                onChange={e => update('numero', e.target.value)}
              />
              <Input
                placeholder="Logradouro (rua/avenida) *"
                required
                value={form.logradouro}
                onChange={e => update('logradouro', e.target.value)}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Bairro"
                  value={form.bairro}
                  onChange={e => update('bairro', e.target.value)}
                />
                <Input
                  placeholder="Complemento"
                  value={form.complemento}
                  onChange={e => update('complemento', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input
                  placeholder="Cidade *"
                  required
                  value={form.cidade}
                  onChange={e => update('cidade', e.target.value)}
                  className="col-span-2"
                />
                <Input
                  placeholder="UF *"
                  required
                  maxLength={2}
                  value={form.estado}
                  onChange={e => update('estado', e.target.value.toUpperCase())}
                />
              </div>
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">Acesso</div>
              <Input
                type="password"
                placeholder="Senha * (mín. 8 caracteres)"
                required
                minLength={8}
                value={form.password}
                onChange={e => update('password', e.target.value)}
              />

              <select
                value={form.segment}
                onChange={e => update('segment', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="mercado">Mercado / Supermercado</option>
                <option value="farmacia">Farmácia / Drogaria</option>
                <option value="padaria">Padaria / Cafeteria</option>
                <option value="restaurante">Restaurante</option>
                <option value="roupa">Loja de roupas</option>
                <option value="autopecas">Auto peças</option>
                <option value="eletronicos">Eletrônicos</option>
                <option value="construcao">Construção</option>
                <option value="outro">Outro</option>
              </select>
            </div>

            <div className="border-t border-border pt-4 space-y-2">
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={form.accept_terms} onChange={e => update('accept_terms', e.target.checked)} className="mt-1" />
                <span>Li e aceito os <a href="/termos" className="text-brand-600 hover:underline">Termos de Uso</a></span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={form.accept_privacy} onChange={e => update('accept_privacy', e.target.checked)} className="mt-1" />
                <span>Li e aceito a <a href="/privacidade" className="text-brand-600 hover:underline">Política de Privacidade</a> (LGPD)</span>
              </label>
            </div>

            <Button type="submit" className="w-full" loading={loading} variant="gradient">
              Criar conta · 14 dias grátis
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-muted-foreground">
            Já tem conta? <Link to="/login" className="text-brand-600 font-medium hover:underline">Entrar</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
